import { RAvailUrlProvider } from '../../common/RAvailUrlProvider';
import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { FxpHttpService, UserInfoService } from '@fxp/fxpservices';
import {
  DirectReportee,
  DirectReporteesGraphQLResponse,
  ResourceSkills,
  ResourceSkillsGraphQLResponse,
  SearchProfilesGraphQLResponse,
} from '../contracts/FMOneProfileContracts';
import { ManagerSuggestion } from '../contracts/FMDashboardContracts';

/**
 * OneProfile (profileskills) GraphQL service for the FM dashboard "Profile Completeness" columns.
 * Talks to the OpGraphQL endpoint (`OpGraphQlUrl`) via FXP `FxpHttpService`, mirroring the singleton
 * + lazy-FXP-resolution pattern of `FMDashboard.service.ts`/`DSE.service.ts` so tests inject a mock
 * `IFMOneProfileService` and never touch the FXP shell. Query/verb/headers are parity of
 * `one-profile.service.js` (`getDirectReporteesSkills`: POST `{query}`; headers X-IsUICall /
 * X-IsSelfCall / X-CurrentUserEntraId). We query per alias (the team aliases already come from
 * GetTeamCalendar), which is the same profileCompleteness + resourceSkills query the legacy runs
 * per reportee.
 */
export interface IFMOneProfileService {
  /** Fetch profile-completeness + skill-completeness + assessed skills for one resource alias. */
  getResourceSkills: (alias: string) => Promise<ResourceSkills | undefined>;
  /** Search managers for the "View other manager's dashboard" typeahead (F8). */
  searchManagers: (searchText: string) => Promise<ManagerSuggestion[]>;
  /**
   * A manager's direct reportees, sorted by display name — the options behind the calendar's
   * "Resource Name" picker when a calendar is opened from the FM dashboard.
   */
  getDirectReportees: (managerAlias: string) => Promise<DirectReportee[]>;
}

const graphQLString = (value: string): string => JSON.stringify(value);

/** Parity of the legacy per-reportee skill query (one-profile.service.js:479-491). */
export const buildResourceSkillsQuery = (alias: string): string =>
  '{ ' +
  `profileCompleteness(alias: ${graphQLString(alias)}) { completionWeightage } ` +
  `resourceSkills(alias: ${graphQLString(alias)}) { ` +
  'assessedSkills { proficiency proficiencyAssignedDate skillName skillId } ' +
  'primarySkills { skillId skillName skillCategory skillHierarchyText proficiency } ' +
  'skillCompleteness { overallWeightage } } }';

/**
 * Copy-and-own of `GrmConfiguration.FMBusinessRoleId` (`grm.config.prod.json:35` — identical in
 * every environment config). Restricts the typeahead to manager business roles, exactly as
 * `fmdashboard.controller.js:67,1802` does.
 */
const FM_BUSINESS_ROLE_IDS = [
  6, 9, 10, 34, 37, 40, 59, 60, 63, 64, 65, 69, 83, 84, 85, 91, 92, 96, 101, 103, 104, 107, 111,
  118, 158, 159, 160, 161, 163, 181, 185, 186, 187, 188, 189, 190, 191, 192, 193, 194, 195, 196,
  197, 206, 207, 28,
];

/** Parity of the legacy `searchUsers` query (one-profile.service.js:1224-1242). */
export const buildSearchManagersQuery = (searchText: string): string =>
  '{ searchProfiles(filter: { ' +
  `searchText: ${graphQLString(searchText)}` +
  `, businessRoles: [${FM_BUSINESS_ROLE_IDS.join(',')}]` +
  ', count: 100' +
  ' }) { contact { displayName alias emailName } role { businessRole businessRoleId } } }';

/**
 * Parity of the legacy `getEngineerDetails` query (one-profile.service.js:1199-1201), narrowed to
 * the two fields the "Resource Name" picker actually binds (see `DirectReportee`).
 */
export const buildDirectReporteesQuery = (managerAlias: string): string =>
  `{ directReporteesDetails(alias: ${graphQLString(managerAlias)}) { alias displayName } }`;

const throwGraphQLErrors = (
  errors?: Array<{ message?: string }> | null
): void => {
  if (!errors || errors.length === 0) {
    return;
  }
  const details = errors
    .map((error) => error.message || 'Unknown GraphQL error')
    .join('; ');
  throw new Error(details);
};

const FMOneProfileService = (function () {
  let _instance: IFMOneProfileService;

  function createInstance(): IFMOneProfileService {
    const urlProvider = new RAvailUrlProvider();
    const httpService = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.FxpHttpService
    ) as FxpHttpService;

    // Parity headers: the profileskills API uses these to resolve self vs on-behalf-of context.
    const buildHeaders = (): Record<string, string> => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      try {
        const userInfo = FxpServiceFactory.getServiceInstance(
          FXPServiceCatalog.UserInfoService
        ) as UserInfoService;
        headers['X-IsUICall'] = 'true';
        headers['X-IsSelfCall'] = String(!userInfo.isActingOnBehalfOf());
        const oid = userInfo.getCurrentUserOID();
        if (oid) {
          headers['X-CurrentUserEntraId'] = oid;
        }
      } catch {
        // No FXP shell (tests/local preview) — send just the content-type header.
      }
      return headers;
    };

    const getResourceSkills = async (
      alias: string
    ): Promise<ResourceSkills | undefined> => {
      if (!alias) {
        return undefined;
      }
      const body = { query: buildResourceSkillsQuery(alias) };
      const response = await httpService.post(
        urlProvider.OpGraphQlUrl(),
        body,
        buildHeaders()
      );
      const responseBody = response?.data as ResourceSkillsGraphQLResponse;
      throwGraphQLErrors(responseBody?.errors);
      const gql = responseBody?.data;
      if (!gql) {
        return undefined;
      }
      return {
        alias: alias.toLowerCase(),
        profileCompleteness: gql.profileCompleteness?.completionWeightage ?? 0,
        skillCompleteness: gql.resourceSkills?.skillCompleteness?.overallWeightage ?? 0,
        assessedSkills: gql.resourceSkills?.assessedSkills ?? [],
        primarySkills: gql.resourceSkills?.primarySkills ?? [],
      };
    };

    const searchManagers = async (searchText: string): Promise<ManagerSuggestion[]> => {
      const term = (searchText || '').trim();
      if (!term) {
        return [];
      }
      const body = { query: buildSearchManagersQuery(term) };
      const response = await httpService.post(
        urlProvider.OpGraphQlUrl(),
        body,
        buildHeaders()
      );
      const responseBody = response?.data as SearchProfilesGraphQLResponse;
      throwGraphQLErrors(responseBody?.errors);
      const profiles = responseBody?.data?.searchProfiles;
      if (!Array.isArray(profiles)) {
        return [];
      }
      return profiles
        .filter((p) => p && p.contact && p.contact.alias)
        .map((p) => ({
          alias: String(p.contact.alias).toLowerCase(),
          name: p.contact.displayName || p.contact.alias,
        }));
    };

    /**
     * Manager's direct reportees for the calendar "Resource Name" picker.
     *
     * Two deliberate departures from `getEngineerDetails` (one-profile.service.js:1199-1216):
     * it swallowed every failure into `[]`, which made its caller's `Invalid User` error branch
     * (calendar.controller.js:3105-3110) dead code — here the error propagates so the picker can
     * hide itself instead of rendering an empty control; and the sort uses `localeCompare` rather
     * than lodash's ordinal `<` comparison, so names are not ordered by ASCII case.
     */
    const getDirectReportees = async (managerAlias: string): Promise<DirectReportee[]> => {
      const alias = (managerAlias || '').trim();
      if (!alias) {
        return [];
      }
      const body = { query: buildDirectReporteesQuery(alias) };
      const response = await httpService.post(
        urlProvider.OpGraphQlUrl(),
        body,
        buildHeaders()
      );
      const responseBody = response?.data as DirectReporteesGraphQLResponse;
      throwGraphQLErrors(responseBody?.errors);
      const reportees = responseBody?.data?.directReporteesDetails;
      if (!Array.isArray(reportees)) {
        return [];
      }
      return reportees
        .filter((r) => r && r.alias)
        .map((r) => ({
          alias: String(r.alias).toLowerCase(),
          displayName: r.displayName || r.alias,
        }))
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
    };

    return { getResourceSkills, searchManagers, getDirectReportees };
  }

  return {
    getSingleton: (): IFMOneProfileService => {
      if (!_instance) {
        _instance = createInstance();
      }
      return _instance;
    },
  };
})();

export default FMOneProfileService;
